var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Standalone login gate for the voice-provider admin console.
 *
 * Renders a fixed-credential login screen until a valid admin token is present,
 * then renders the console. Independent of the normal user/IAM login — this page
 * is admin-only. The token is validated server-side on mount and whenever an API
 * call reports 401 (token expired), which bounces back to the login screen.
 */
import { useConfigContext } from '@/hoc/context/config-provider';
import { Loader2, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../ui';
import { isVoiceAdminAuthed, setVoiceAdminBaseUrl, voiceAdminCheckSession, voiceAdminLogin, } from './voice-admin-api';
export const VoiceAdminGate = ({ children }) => {
    const { config } = useConfigContext();
    const [phase, setPhase] = useState('checking');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    // Point the admin client at this environment's API base once.
    setVoiceAdminBaseUrl(config.apiBaseUrl);
    useEffect(() => {
        let alive = true;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            const ok = isVoiceAdminAuthed() && (yield voiceAdminCheckSession());
            if (alive)
                setPhase(ok ? 'authed' : 'login');
        }))();
        // Any API 401 during use kicks back to the login screen.
        const onUnauthorized = () => setPhase('login');
        window.addEventListener('voice-admin-unauthorized', onUnauthorized);
        return () => {
            alive = false;
            window.removeEventListener('voice-admin-unauthorized', onUnauthorized);
        };
    }, []);
    const submit = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            yield voiceAdminLogin(email.trim(), password);
            setPhase('authed');
            setPassword('');
        }
        catch (err) {
            setError((err === null || err === void 0 ? void 0 : err.error) || 'Login failed');
        }
        finally {
            setSubmitting(false);
        }
    });
    if (phase === 'checking') {
        return (_jsx("div", { className: 'flex h-[60vh] items-center justify-center', children: _jsx(Loader2, { className: 'h-6 w-6 animate-spin text-dash-muted' }) }));
    }
    if (phase === 'login') {
        return (_jsx("div", { className: 'flex min-h-[70vh] items-center justify-center p-6', children: _jsxs("form", { onSubmit: submit, className: 'w-full max-w-sm space-y-4 rounded-2xl border border-dash-border bg-dash-card p-6 shadow-sm', children: [_jsxs("div", { className: 'flex items-center gap-2', children: [_jsx("span", { className: 'flex h-9 w-9 items-center justify-center rounded-full bg-dash-surface', children: _jsx(Lock, { className: 'h-4 w-4 text-dash-muted' }) }), _jsxs("div", { children: [_jsx("h2", { className: 'text-lg font-semibold text-dash-text', children: "Admin sign in" }), _jsx("p", { className: 'text-xs text-dash-muted', children: "Voice & Proctoring console" })] })] }), _jsxs("div", { children: [_jsx("label", { className: 'mb-1 block text-xs font-medium text-dash-muted', children: "Email" }), _jsx("input", { type: 'email', autoComplete: 'username', className: 'w-full rounded-md border border-dash-border bg-dash-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent', value: email, onChange: (e) => setEmail(e.target.value), placeholder: 'admin@vriti.ai', required: true })] }), _jsxs("div", { children: [_jsx("label", { className: 'mb-1 block text-xs font-medium text-dash-muted', children: "Password" }), _jsx("input", { type: 'password', autoComplete: 'current-password', className: 'w-full rounded-md border border-dash-border bg-dash-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent', value: password, onChange: (e) => setPassword(e.target.value), required: true })] }), error && _jsx("p", { className: 'text-sm text-dash-dangerInk', children: error }), _jsx(Button, { type: 'submit', variant: 'primary', isLoading: submitting, className: 'w-full', children: "Sign in" })] }) }));
    }
    // Logout now lives in the admin console header (AdminConsoleLayout), which
    // calls clearVoiceAdmin() + dispatches 'voice-admin-unauthorized' — the same
    // event this gate already listens for to return to the login screen. So the
    // authed branch just renders the console full-bleed (no overlay button).
    return _jsx("div", { className: 'h-full', children: children });
};
export default VoiceAdminGate;
